/**
 * CME 2m volumes behind the chart, for the time-of-day rel-vol baseline only
 * (🦈, 🐋, 🐋🦈, 🪝 spike factor; issue #593).
 *
 * The chart loads ~1 day of 2m candles (subscribe sends 720), but the time of
 * day baseline needs >= 5 (up to 10) earlier sessions at the same Chicago
 * slot. This fetches CME_BASELINE_CANDLES (~12.5 sessions, ~1.07 MB) of 2m
 * candles from `GET /api/historical/{symbol}?count=N` once per symbol per
 * Chicago day and keeps only {time, volume} of the completed ones. It is
 * NEVER put into the chart's candle series: utils/cmeVolumeSpikes
 * `withBaselineHistory` places it in front of the chart's bars for the rel vol
 * walk, and only the chart's bars get rel vols.
 *
 * The live chart bars cover everything after the fetch, so the history never
 * needs to be newer than the chart's first candle; the daily refetch only
 * keeps the window from drifting (a tab left open for days).
 */
import { create } from 'zustand';
import { getApiBaseUrl } from '../config/ports';
import { CME_BASELINE_CANDLES } from '../utils/relVolBaseline';
import type { VolumeBar } from '../utils/cmeVolumeSpikes';

export type BaselineHistoryStatus = 'loading' | 'ready' | 'error';

export interface CmeBaselineHistory {
  status: BaselineHistoryStatus;
  /** Completed 2m candles, oldest first, seconds. */
  bars: VolumeBar[];
  /** Chicago date (YYYY-MM-DD) the history was fetched for. */
  day: string;
  error: string | null;
}

interface CmeBaselineHistoryState {
  bySymbol: Record<string, CmeBaselineHistory>;
  /** Fetch the symbol's history unless it is loaded (or loading) for today. */
  ensure: (symbol: string, nowMs?: number) => Promise<void>;
}

const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
export const chicagoDay = (ms: number): string => dayFmt.format(ms);

interface RawCandle {
  timestamp?: unknown;
  volume?: unknown;
  isCompleted?: unknown;
  _isCompleted?: unknown;
  is_completed?: unknown;
}

/** Completed candles -> {time (s), volume}, oldest first. */
export function parseBaselineCandles(raw: unknown): VolumeBar[] {
  if (!Array.isArray(raw)) return [];
  const out: VolumeBar[] = [];
  for (const c of raw as RawCandle[]) {
    if (!c || typeof c.timestamp !== 'number' || !Number.isFinite(c.timestamp)) continue;
    const done = c.isCompleted ?? c._isCompleted ?? c.is_completed;
    if (done === false) continue;
    out.push({ time: Math.floor(c.timestamp / 1000), volume: typeof c.volume === 'number' && Number.isFinite(c.volume) ? c.volume : 0 });
  }
  return out.sort((a, b) => a.time - b.time);
}

const inflight = new Map<string, Promise<void>>();

export const useCmeBaselineHistoryStore = create<CmeBaselineHistoryState>((set, get) => ({
  bySymbol: {},

  ensure: async (symbol, nowMs = Date.now()) => {
    if (!symbol) return;
    const day = chicagoDay(nowMs);
    const cur = get().bySymbol[symbol];
    const pending = inflight.get(symbol);
    if (pending) return pending;
    if (cur && cur.day === day && cur.status !== 'error') return;
    const setEntry = (e: Partial<CmeBaselineHistory>) =>
      set((s) => ({
        bySymbol: { ...s.bySymbol, [symbol]: { ...(s.bySymbol[symbol] ?? { bars: [], day, error: null, status: 'loading' }), ...e } },
      }));
    // Keep the previous day's bars while the refresh loads (still a valid baseline).
    setEntry({ status: 'loading', day, error: null });
    const run = (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/historical/${encodeURIComponent(symbol)}?count=${CME_BASELINE_CANDLES}`);
        const body = await res.json().catch(() => null);
        if (!res.ok || !body || typeof body.error === 'string') throw new Error(body?.error ?? `HTTP ${res.status}`);
        setEntry({ status: 'ready', bars: parseBaselineCandles(body.candles), day, error: null });
      } catch (e) {
        setEntry({ status: 'error', error: e instanceof Error ? e.message : String(e) });
      } finally {
        inflight.delete(symbol);
      }
    })();
    inflight.set(symbol, run);
    return run;
  },
}));
